/*
 * Legacy customer history views (WU PROJ-0062-WU-LEGACY-HISTORY-01; LOCKED
 * PROGRAMME-DEC-LEGACY-CUSTOMER-MIGRATION-V1 sinks 2/5). Pure presentational —
 * the screens own the read (useLegacyHistory) and pass the visible block in,
 * so a null block renders NOTHING (flag off / not linked / unknown / any
 * failure — never an error UI).
 *
 *  - LegacyHistorySection (Profile): "Your Trumin history". has_activity ⇒
 *    Total paid per currency (one line each — never converted or summed),
 *    counts, first / most recent event dates and the source note; a linked
 *    account with no past activity ⇒ one quiet line.
 *  - LegacyHistoryOrdersCard (Orders → Past events): "Earlier with Trumin",
 *    compact and read-only (no links — drill-down is phase 2); only when
 *    has_activity.
 *
 * Money is GROSS (refunds not netted, ruling 1) — "Total paid", never "net".
 */
import type { CSSProperties } from 'react'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import { formatMinorAmount, formatPlainDate } from '../data/legacyHistory'
import type { MyLegacyHistory } from '../data/myApi'

const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15,
}
const statLabel: CSSProperties = { display: 'block', fontSize: 11, color: 'var(--text-secondary)' }
const statValue: CSSProperties = { display: 'block', fontSize: 15, fontWeight: 800 }

function TotalsPaid({ legacy, t, testId }: { legacy: MyLegacyHistory; t: TranslateFn; testId: string }) {
  const { lang } = useLocale()
  if (legacy.totals_by_currency.length === 0) return null
  return (
    <div data-testid={testId}>
      <span style={statLabel}>{t('history.totalPaid')}</span>
      {legacy.totals_by_currency.map((c) => (
        <span key={c.currency} data-testid="history-total-line" data-currency={c.currency} style={statValue}>
          {formatMinorAmount(c.spend_gross_minor, c.currency, lang)}
        </span>
      ))}
    </div>
  )
}

export function LegacyHistorySection({ legacy }: { legacy: MyLegacyHistory | null }) {
  const { t, lang } = useLocale()
  if (!legacy) return null
  return (
    <div id="profile.history" data-testid="profile-history">
      <div style={kickerStyle}>{t('history.profile.kicker')}</div>
      {!legacy.has_activity ? (
        <div data-testid="profile-history-empty" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px 22px' }}>
          {t('history.noActivity')}
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 15, marginBottom: 22 }}>
          <TotalsPaid legacy={legacy} t={t} testId="profile-history-totals" />
          <div
            data-testid="profile-history-counts"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px', marginTop: legacy.totals_by_currency.length ? 14 : 0 }}
          >
            {([
              ['events', 'history.eventsAttended', legacy.events_attended],
              ['results', 'history.results', legacy.results_count],
              ['tickets', 'history.tickets', legacy.tickets_purchased],
              ['orders', 'history.orders', legacy.orders_count],
            ] as const).map(([k, label, n]) => (
              <div key={k} data-testid="profile-history-count" data-k={k}>
                <span style={statLabel}>{t(label)}</span>
                <span style={statValue}>{n.toLocaleString(lang === 'ar' ? 'ar' : 'en-GB')}</span>
              </div>
            ))}
          </div>
          {(legacy.first_event_date || legacy.last_event_date) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px', marginTop: 14 }}>
              {legacy.first_event_date && (
                <div data-testid="profile-history-first">
                  <span style={statLabel}>{t('history.firstEvent')}</span>
                  <span style={{ ...statValue, fontSize: 13.5 }}>{formatPlainDate(legacy.first_event_date, lang)}</span>
                </div>
              )}
              {legacy.last_event_date && (
                <div data-testid="profile-history-last">
                  <span style={statLabel}>{t('history.lastEvent')}</span>
                  <span style={{ ...statValue, fontSize: 13.5 }}>{formatPlainDate(legacy.last_event_date, lang)}</span>
                </div>
              )}
            </div>
          )}
          <div data-testid="profile-history-note" style={{ fontSize: 11, color: 'var(--text-faint)', lineHeight: 1.5, marginTop: 14 }}>
            {t('history.note')}
          </div>
        </div>
      )}
    </div>
  )
}

export function LegacyHistoryOrdersCard({ legacy }: { legacy: MyLegacyHistory | null }) {
  const { t, lang } = useLocale()
  if (!legacy || !legacy.has_activity) return null
  return (
    <div
      data-testid="orders-legacy-card"
      style={{
        background: 'var(--tru-surface-2)', border: 'var(--tru-slate-border)',
        borderRadius: 'var(--tru-slate-radius)', padding: '14px 16px', marginBlockEnd: 'var(--tru-card-gap)',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, marginBlockEnd: 2 }}>{t('history.orders.title')}</div>
      <div style={{ fontSize: 11, color: 'var(--tru-text-tertiary)', marginBlockEnd: 10 }}>{t('history.note')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
        <div data-testid="orders-legacy-events">
          <span style={{ ...statLabel, color: 'var(--tru-text-secondary)' }}>{t('history.eventsAttended')}</span>
          <span style={statValue}>{legacy.events_attended.toLocaleString(lang === 'ar' ? 'ar' : 'en-GB')}</span>
        </div>
        <TotalsPaid legacy={legacy} t={t} testId="orders-legacy-totals" />
      </div>
    </div>
  )
}
